<template>
  <div>
    <form @submit.prevent="submitForm">

      <div class="mb-3">
        <label for="teacher_id" class="form-label">Teacher ID</label>
        <input type="text" id="teacher_id" v-model="localTeacher.id" class="form-control" required disabled>
      </div>

      <!-- Teacher NIC Input -->
      <div class="mb-3">
        <label for="teacher_nic" class="form-label">Teacher NIC</label>
        <input type="text" id="teacher_nic" v-model="localTeacher.teacher_nic" class="form-control" required>
      </div>

      <!-- Birth Day Input -->
      <div class="mb-3">
        <label for="birth_day" class="form-label">Birth Day</label>
        <input type="date" id="birth_day" v-model="localTeacher.birth_day" class="form-control" required>
      </div>

      <!-- Address Input -->
      <div class="mb-3">
        <label for="address" class="form-label">Address</label>
        <input type="text" id="address" v-model="localTeacher.address" class="form-control" required>
      </div>

      <!-- Contact Input -->
      <div class="mb-3">
        <label for="contact" class="form-label">Contact</label>
        <input type="text" id="contact" v-model="localTeacher.contact" class="form-control" required>
      </div>

      <div>
        <div v-if="localTeacher.teach_institute && localTeacher.teach_institute.length > 0">
          <div v-for="(institute, index) in localTeacher.teach_institute" :key="index" class="mb-3">
            <label :for="'instituteSelect' + index" class="form-label">Change Institute</label>
            <select 
              :id="'instituteSelect' + index" 
              v-model="institute.institute_id" 
              @change="updateInstitute(institute, institute.institute_id)" 
              class="form-select">
              <option value="" disabled>Select an Institute</option>
              <option v-for="inst in institutes" :key="inst.id" :value="inst.id">
                {{ inst.institute_name }}
              </option>
            </select>

            <label class="form-label">Selected Institute Name</label>
            <input type="text" 
              :value="getInstituteName(institute.institute_id)" 
              class="form-control" 
              readonly>
          </div>
        </div>
      </div>

      <!-- User Details Section -->
      <div v-if="localTeacher.user">
        <h3>User Details</h3>
        <div class="mb-3">
          <label for="firstName" class="form-label">First Name</label>
          <input type="text" id="firstName" v-model="localTeacher.user.first_name" class="form-control">
        </div>
        <div class="mb-3">
          <label for="lastName" class="form-label">Last Name</label>
          <input type="text" id="lastName" v-model="localTeacher.user.last_name" class="form-control">
        </div>
        <div class="mb-3">
          <label for="email" class="form-label">Email</label>
          <input type="email" id="email" v-model="localTeacher.user.email" class="form-control">
        </div>
        <div class="mb-3">
          <label for="profilePhoto" class="form-label">Profile Photo</label>
          <input type="file" id="profilePhoto" @change="handleFileUpload" class="form-control">
          <div v-if="previewImage" class="mt-2">
            <img :src="previewImage" alt="Profile Photo Preview" class="img-fluid">
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <button type="submit" class="btn btn-primary" :disabled="loading">
        <span v-if="loading">Saving...</span>
        <span v-else>Save Changes</span>
      </button>
    </form>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    teacher: {
      type: Object,
      default: () => ({
        id: '',
        teacher_nic: '',
        birth_day: '',
        address: '',
        contact: '',
        institute_id: null,
        user: {
          first_name: '',
          last_name: '',
          email: '',
          profile_photo_url: ''
        },
        teach_institutes: [],
      })
    }
  },
  data() {
    return {
      selectedInstituteIds: [],
      localTeacher: this.teacher ? { ...this.teacher } : {
        id: '',
        teacher_nic: '',
        birth_day: '',
        address: '',
        contact: '',
        institute_id: null,
        user: {
          first_name: '',
          last_name: '',
          email: '',
          profile_photo_url: ''
        },
        teach_institutes: [{ institute_id: null }]
      },
      selectedFile: null,
      previewImage: this.teacher?.user?.profile_photo_url || '',
      institutes: [],
      errorMessage: '',
      loading: false // Track loading state
    };
  },
  watch: {
    teacher(newTeacher) {
      if (newTeacher) {
        this.localTeacher = { ...newTeacher };
        this.previewImage = newTeacher?.user?.profile_photo_url || '';
      }
    }
  },
  mounted() {
    this.fetchInstitutes();
  },
  methods: {
    async fetchInstitutes() {
      try {
        const response = await axios.get('/get-all-institutes');
        this.institutes = response.data;
      } catch (error) {
        this.errorMessage = 'Error fetching institutes.';
      }
    },
    getInstituteName(instituteId) {
      const selectedInstitute = this.institutes.find(inst => inst.id === instituteId);
      return selectedInstitute ? selectedInstitute.institute_name : 'N/A';
    },
    updateInstitute(institute, instituteId) {
      const index = this.selectedInstituteIds.indexOf(instituteId);
      if (index === -1) {
        this.selectedInstituteIds.push(instituteId);
      } else {
        this.selectedInstituteIds.splice(index, 1);
      }

      console.log(`Institute changed to ID: ${instituteId}`);
      console.log('Selected Institute IDs:', this.selectedInstituteIds);
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.previewImage = URL.createObjectURL(file);
      }
    },
    async submitForm() {
      this.loading = true; // Set loading state to true
      try {
        const formData = new FormData();
        formData.append('teacher_id', this.localTeacher.id);
        formData.append('teacher_nic', this.localTeacher.teacher_nic);
        formData.append('birth_day', this.localTeacher.birth_day);
        formData.append('address', this.localTeacher.address);
        formData.append('contact', this.localTeacher.contact);
        formData.append('first_name', this.localTeacher.user.first_name);
        formData.append('last_name', this.localTeacher.user.last_name);
        formData.append('email', this.localTeacher.user.email);
        formData.append('user_id', this.localTeacher.user?.id || '');

        this.selectedInstituteIds.forEach((id) => {
          formData.append('selected_institute_ids[]', id);
        });

        if (this.selectedFile) {
          formData.append('profile_photo', this.selectedFile);
        }

        await axios.post('/update-teacher', formData);
        alert('Teacher updated successfully');
      } catch (error) {
        console.error('Error updating teacher:', error);
        alert('Failed to update teacher');
      }
      finally {
        this.loading = false; // Reset loading state after completion
      }
    }
  }
};
</script>

<style scoped>
.img-fluid {
  max-width: 100%;
  height: auto;
}
</style>
